import { z } from 'zod';
import { AuditActorSchema } from './audit';
import {
  type CanonicalObjectType,
  ComponentTypeSchema,
  ControlKindSchema,
  ControlPlaneSchema,
  DataClassificationSchema,
  DataSubjectSchema,
  EnvironmentKindSchema,
  PrioritySchema,
  TrustBoundaryKindSchema,
  TrustLevelSchema,
  UnknownKindSchema,
} from './enums';
import {
  ActorIdSchema,
  AssessmentIdSchema,
  AssetIdSchema,
  AssumptionIdSchema,
  ComponentIdSchema,
  ConflictGroupIdSchema,
  ConflictIdSchema,
  ControlFlowIdSchema,
  DataFlowIdSchema,
  EnvironmentIdSchema,
  EvidenceRefSchema,
  ExistingControlIdSchema,
  idKindOf,
  RunIdSchema,
  SafetyFunctionIdSchema,
  TenantIdSchema,
  TrustBoundaryIdSchema,
  UnknownIdSchema,
} from './ids';
import { ArtefactCoverageSchema } from './coverage';
import { type EvidenceCitation, EvidenceCitationSchema, locatorLocatesSomething } from './locator';
import {
  ClientQuestionSchema,
  NonEmptyStringSchema,
  TimestampSchema,
  TristateSchema,
} from './primitives';
import {
  attributeProvenanceSchema,
  attributesMissingProvenance,
  establishedAttributes,
  type Provenance,
  type Provenanced,
  ProvenanceSchema,
} from './provenance';

/**
 * The canonical model is the linchpin (DESIGN_SPEC §7): every downstream worker
 * reads it, and every finding grounds against it.
 *
 * Two rules shape everything here:
 *
 *  1. **No schema-level defaults.** Parsing a canonical model returns exactly
 *     what was stored. An evidence pack has to be able to claim that what the
 *     approver saw is what was persisted, and a default silently filling a field
 *     would break that claim.
 *  2. **Uncertainty is preserved, never discarded.** Attributes are `Tristate`
 *     rather than boolean, and every object carries `provenance` recording
 *     whether its values were labelled by a human or artefact, inferred, or
 *     assumed. That distinction is the structural input to derived confidence
 *     (§8.4) — without it, confidence would have to be guessed by a model.
 */

/**
 * Why we believe an object's attributes — now shared with extraction
 * (docs/DECISIONS.md ZD-014), so it lives in `./provenance`. Re-exported here
 * because every canonical object carries one and this is where readers of the
 * canonical model look for it.
 */
export { type Provenance, ProvenanceSchema };

/** A flow may start or end at a component or at an actor. */
export const FlowEndpointSchema = z.union([ComponentIdSchema, ActorIdSchema]);

/**
 * Attribute vocabularies (docs/DECISIONS.md ZD-020).
 *
 * Each list is the closed set of attribute names that object's
 * `attribute_provenance` may key on. Written out rather than derived from the
 * schema shape so the keys carry literal types and a typo is a *compile* error
 * at every call site; `canonical.test.ts` asserts each list against its schema's
 * own fields, so the two cannot drift apart silently.
 *
 * Identity and provenance fields are excluded: `component_id` is not a fact
 * about the component, it is the handle we refer to it by, and `provenance`
 * already answers how the object's existence was established.
 */
export const SYSTEM_ATTRIBUTES = [
  'name',
  'description',
  'internet_facing',
  'multi_tenant',
] as const;

export const ENVIRONMENT_ATTRIBUTES = [
  'name',
  'kind',
  'cloud',
  'region',
  'data_residency',
] as const;

export const COMPONENT_ATTRIBUTES = [
  'name',
  'type',
  'description',
  'environment_id',
  'internet_facing',
  'technologies',
] as const;

export const ACTOR_ATTRIBUTES = ['name', 'trust', 'description'] as const;

export const ASSET_ATTRIBUTES = [
  'name',
  'data_classification',
  'data_subject',
  'description',
  'resides_in',
] as const;

export const DATA_FLOW_ATTRIBUTES = [
  'from',
  'to',
  'protocol',
  'data',
  'asset_ids',
  'encrypted',
  'authenticated',
  'crosses_boundary_ids',
] as const;

export const CONTROL_FLOW_ATTRIBUTES = [
  'from',
  'to',
  'plane',
  'protocol',
  'privileged',
  'authenticated',
  'crosses_boundary_ids',
] as const;

export const TRUST_BOUNDARY_ATTRIBUTES = ['name', 'kind', 'separates'] as const;

export const EXISTING_CONTROL_ATTRIBUTES = ['name', 'description', 'kinds', 'applies_to'] as const;

export const SAFETY_FUNCTION_ATTRIBUTES = ['name', 'description', 'protects'] as const;

/**
 * The handle a conflict uses to name the system itself.
 *
 * The system is the one modelled thing with no minted id: it is the *subject*
 * of the assessment rather than an object in it, so it is absent from
 * `indexCanonicalObjects` and cannot be grounded on. It can still be
 * contradicted — two documents can disagree about whether the system is
 * internet-facing — and a contradiction with no conflict record is exactly the
 * hole ZD-020's integrity check exists to close, so it needs a handle.
 */
export const SYSTEM_OBJECT_ID = 'system';

/**
 * The system under assessment.
 *
 * It carries provenance like every other object, which it did not until
 * docs/DECISIONS.md ZD-021 — it was the one object in the model whose
 * attributes had no recorded basis, so `system.internet_facing` was a
 * `Tristate` nothing could derive confidence from.
 */
export const SystemSchema = z.object({
  name: NonEmptyStringSchema,
  description: z.string(),
  internet_facing: TristateSchema,
  multi_tenant: TristateSchema,
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(SYSTEM_ATTRIBUTES),
});
export type System = z.infer<typeof SystemSchema>;

/** Deployment context (docs/DECISIONS.md ZD-006). */
export const EnvironmentSchema = z.object({
  environment_id: EnvironmentIdSchema,
  name: NonEmptyStringSchema,
  kind: EnvironmentKindSchema,
  cloud: z.string().nullable(),
  region: z.string().nullable(),
  data_residency: z.string().nullable(),
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(ENVIRONMENT_ATTRIBUTES),
});
export type Environment = z.infer<typeof EnvironmentSchema>;

export const ComponentSchema = z.object({
  component_id: ComponentIdSchema,
  name: NonEmptyStringSchema,
  type: ComponentTypeSchema,
  description: z.string(),
  environment_id: EnvironmentIdSchema.nullable(),
  internet_facing: TristateSchema,
  technologies: z.array(z.string()),
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(COMPONENT_ATTRIBUTES),
});
export type Component = z.infer<typeof ComponentSchema>;

export const ActorSchema = z.object({
  actor_id: ActorIdSchema,
  name: NonEmptyStringSchema,
  trust: TrustLevelSchema,
  description: z.string(),
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(ACTOR_ATTRIBUTES),
});
export type Actor = z.infer<typeof ActorSchema>;

export const AssetSchema = z.object({
  asset_id: AssetIdSchema,
  name: NonEmptyStringSchema,
  data_classification: DataClassificationSchema,
  data_subject: DataSubjectSchema,
  description: z.string(),
  resides_in: z.array(ComponentIdSchema),
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(ASSET_ATTRIBUTES),
});
export type Asset = z.infer<typeof AssetSchema>;

export const DataFlowSchema = z.object({
  flow_id: DataFlowIdSchema,
  from: FlowEndpointSchema,
  to: FlowEndpointSchema,
  protocol: z.string().nullable(),
  data: z.string(),
  asset_ids: z.array(AssetIdSchema),
  encrypted: TristateSchema,
  authenticated: TristateSchema,
  crosses_boundary_ids: z.array(TrustBoundaryIdSchema),
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(DATA_FLOW_ATTRIBUTES),
});
export type DataFlow = z.infer<typeof DataFlowSchema>;

/**
 * Management- and control-plane paths, modelled separately from data flows
 * (docs/DECISIONS.md ZD-006). A deployment pipeline pushing to production is not
 * a data flow, and treating it as one misclassifies a whole class of Tampering
 * and Elevation-of-Privilege threats.
 */
export const ControlFlowSchema = z.object({
  ctrlflow_id: ControlFlowIdSchema,
  from: FlowEndpointSchema,
  to: FlowEndpointSchema,
  plane: ControlPlaneSchema,
  protocol: z.string().nullable(),
  privileged: TristateSchema,
  authenticated: TristateSchema,
  crosses_boundary_ids: z.array(TrustBoundaryIdSchema),
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(CONTROL_FLOW_ATTRIBUTES),
});
export type ControlFlow = z.infer<typeof ControlFlowSchema>;

export const TrustBoundarySchema = z.object({
  boundary_id: TrustBoundaryIdSchema,
  /**
   * Nullable, matching extraction (docs/DECISIONS.md ZD-019, ZD-023).
   *
   * ZD-019 made the extracted form nullable because a dashed rectangle drawn
   * round part of a diagram with no text on it is a real boundary and has no
   * name. ZD-023 settles the canonical side the same way, on ZD-014's argument:
   * extraction and the canonical model share `Provenance` precisely so that
   * normalisation *lifts* rather than translates, and a field extraction can
   * hold but the model cannot is a translation at exactly that boundary.
   *
   * A label for a human comes from `describeBoundary()` at render time. It is
   * deliberately not stored: "the boundary around the Yard Controller" is a
   * phrase for a reader, not a belief about the architecture, and storing it as
   * an `inferred` fact would read in a report exactly like a name the document
   * supplied.
   */
  name: NonEmptyStringSchema.nullable(),
  kind: TrustBoundaryKindSchema,
  /** Components, actors or environments the boundary separates. */
  separates: z.array(z.union([ComponentIdSchema, ActorIdSchema, EnvironmentIdSchema])),
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(TRUST_BOUNDARY_ATTRIBUTES),
});
export type TrustBoundary = z.infer<typeof TrustBoundarySchema>;

/** A control observed in the architecture — not a control we are recommending. */
export const ExistingControlSchema = z.object({
  control_id: ExistingControlIdSchema,
  name: NonEmptyStringSchema,
  description: z.string(),
  /**
   * What this control actually does (docs/DECISIONS.md ZD-039).
   *
   * Without it, every *"no observed control applies to this"* predicate was
   * satisfied by any control at all, so a control of one kind suppressed a
   * finding of another and the finding fired on a different object — a report
   * pointing a client away from a real exposure, which is worse than silence
   * because nothing in it looks wrong (ZD-037 §4).
   *
   * **A collection, because a control routinely does two things.** *"Accepts
   * mutual TLS only"* is authentication and encryption in transit; *"encrypted
   * at rest ... automated backups retained for 35 days"* is two controls in one
   * sentence. A scalar would have nowhere to put the second half, which is
   * ZD-021's manufactured absence.
   *
   * **Empty and unestablished is the honest state for "the document does not
   * say."** Nothing may establish it empty (`emptyCollectionsClaimingProvenance`
   * at the extraction layer, ZD-036 §2), so a pattern reading it gets `unknown`
   * and asks (ZD-029 §3) rather than asserting that nothing of that kind
   * protects the object.
   */
  kinds: z.array(ControlKindSchema),
  /**
   * Components, flows **and trust boundaries** (docs/DECISIONS.md ZD-030).
   *
   * "All traffic crossing the internet edge uses TLS 1.3" is one of the
   * commonest forms a design document states a control in, and until ZD-030
   * there was nowhere to put it — so normalisation dropped the reference and
   * raised an unknown, which is a manufactured absence of exactly ZD-021's
   * kind. Which flows a boundary-scoped control covers is **not** stored here:
   * `controlsCovering` computes it, because the document made one claim and
   * expanding it into one entry per crossing flow would put seven in the model,
   * each needing a citation the source never gave.
   */
  applies_to: z.array(
    z.union([ComponentIdSchema, DataFlowIdSchema, ControlFlowIdSchema, TrustBoundaryIdSchema]),
  ),
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(EXISTING_CONTROL_ATTRIBUTES),
});
export type ExistingControl = z.infer<typeof ExistingControlSchema>;

export const AssumptionSchema = z.object({
  assumption_id: AssumptionIdSchema,
  statement: NonEmptyStringSchema,
  basis: NonEmptyStringSchema,
  related_object_ids: z.array(z.string()),
});
export type Assumption = z.infer<typeof AssumptionSchema>;

export const UnknownSchema = z.object({
  unknown_id: UnknownIdSchema,
  /**
   * The question, and it must be one (ZD-103). `renderInputGaps` puts this in
   * front of a client under a heading that calls it a question; `normalise`
   * writes seven of these directly, so the rule has to hold here as well as on
   * the extracted form.
   */
  question_hint: ClientQuestionSchema,
  /**
   * What sort of gap this is (docs/DECISIONS.md ZD-057 §5, ZD-106 §3).
   *
   * The full vocabulary, against `ExtractedUnknown`'s narrowed one: this
   * layer is the only one that can write `sources_disagree`, because
   * assembling a disagreement out of several sources is normalisation's job
   * — the same division `Conflict` already runs on.
   */
  kind: UnknownKindSchema,
  priority: PrioritySchema,
  related_object_ids: z.array(z.string()),
  /**
   * Lifted from `ExtractedUnknown`, never re-derived (ZD-014, ZD-107 §3).
   *
   * The same field with the same promise: the single attribute this gap is
   * about, or `null` where it is not about exactly one. It is on **both**
   * layers rather than on extraction alone because the canonical model is the
   * record a reviewer and a report read, and a field added at one layer and
   * dropped at the next is this project's own "a rule established at one layer
   * does not travel to the next by being true", inverted — the loss would be
   * silent and the door would only be half shut.
   *
   * Unlike `kind`, there is nothing to widen here: extraction and normalisation
   * name attributes out of the same vocabulary, so the lift is a copy.
   * Normalisation's own unknowns fill it where the gap is about one attribute —
   * a `sources_disagree` names the attribute the sources disagree about — and
   * `null` where it is about an object's identity or a whole document.
   */
  about_attribute: z.string().min(1).nullable(),
  resolved_by_answer_id: z.string().nullable(),
});
export type Unknown = z.infer<typeof UnknownSchema>;

/**
 * Reserved for the OT pack (docs/DECISIONS.md ZD-010) — a safety function
 * genuinely is not a component, an asset or a flow. Optional in effect: an
 * empty `safety_functions` collection is the norm in MVP, and nothing grounds
 * on this type until the OT pack exists (it is not in `PRIMARY_GROUNDING_TYPES`).
 */
export const SafetyFunctionSchema = z.object({
  safety_function_id: SafetyFunctionIdSchema,
  name: NonEmptyStringSchema,
  description: z.string(),
  protects: z.array(z.string()),
  provenance: ProvenanceSchema,
  attribute_provenance: attributeProvenanceSchema(SAFETY_FUNCTION_ATTRIBUTES),
});
export type SafetyFunction = z.infer<typeof SafetyFunctionSchema>;

/**
 * One assertion competing inside a `Conflict` — what was claimed, and by what.
 *
 * It carries `asserted_by_run_id` for `Provenance`'s reason (ZD-046), and the
 * mirror question answers here specifically rather than by analogy. A merged
 * `contradicted` attribute's own provenance names the **normaliser's** run, not
 * the two extraction runs that read the two documents — so without a run on each
 * assertion, *"which model version read the LLD and said this"* is unanswerable
 * from anything the conflict holds. That is a manufactured absence in the one
 * record whose entire content is "these two sources disagree, here and here".
 */
export const CompetingAssertionSchema = z.object({
  value: NonEmptyStringSchema,
  citation: EvidenceCitationSchema,
  asserted_by: AuditActorSchema,
  asserted_at: TimestampSchema,
  asserted_by_run_id: RunIdSchema.nullable(),
});
export type CompetingAssertion = z.infer<typeof CompetingAssertionSchema>;

/**
 * Records a contradiction between sources (docs/DECISIONS.md ZD-013) — e.g.
 * the HLD says a flow is internal, the LLD says it is internet-facing. Every
 * attribute whose provenance basis is `contradicted` must have a matching
 * conflict record here (`checkModelIntegrity`), and it stays until an answer
 * resolves it.
 *
 * **One record per attribute, and that is deliberate** (docs/DECISIONS.md
 * ZD-022). Its entire content is "these two sources disagree, *here* and
 * *here*", and the citations that say where differ attribute by attribute — in
 * the Tarnmouth pair the component's disagreement is cited at HLD L105-L108
 * against LLD page 1, and the protocol's at L78-L86 against page 2. Widening
 * the record to hold several attributes would either lose that pairing or leave
 * a list of assertions with no way to say which two disagree about what.
 */
export const ConflictSchema = z.object({
  conflict_id: ConflictIdSchema,
  about_object_id: z.string(),
  attribute: NonEmptyStringSchema,
  /**
   * Ties together the conflicts produced by **one** documentation disagreement
   * (docs/DECISIONS.md ZD-022).
   *
   * The Tarnmouth manifest feed moving to the public load balancer is one
   * problem a client has, and it lands on three attributes across two objects,
   * because the canonical model expresses exposure on `Component.internet_facing`
   * and on `DataFlow.crosses_boundary_ids` and `DataFlow.protocol`. Without a
   * grouping key a report shows three findings and a coverage metric counts the
   * disagreement three times.
   *
   * Null when a disagreement genuinely concerns a single attribute: a group of
   * one is ceremony, not information. There is deliberately no `ConflictGroup`
   * object — see ZD-022 — because the thing such an object would carry is a
   * summary sentence, and a deterministic normaliser writing prose is either a
   * template or an LLM call. The sentence is rendered from the members instead.
   */
  group_id: ConflictGroupIdSchema.nullable(),
  competing_assertions: z.array(CompetingAssertionSchema).min(2),
  resolved_by_answer_id: z.string().nullable(),
});
export type Conflict = z.infer<typeof ConflictSchema>;

/** Every conflict belonging to one documentation disagreement (ZD-022). */
export function conflictsInGroup(model: CanonicalModel, groupId: string): Conflict[] {
  return model.conflicts.filter((conflict) => conflict.group_id === groupId);
}

/**
 * Every canonical object carries the ZD-020 pair, with its exact attribute
 * vocabulary, checked at compile time.
 *
 * Same posture as the `R2Bucket extends R2BucketLike` assertion in
 * `apps/api/src/env.ts`: `attributeConfidence` and `provenanceOfAttribute` are
 * written against `Provenanced`, so if a schema here loses
 * `attribute_provenance` the package stops compiling rather than the two
 * quietly drifting apart.
 *
 * **Key-set equality in both directions, not one-way assignability.** A single
 * `T extends Provenanced<A>` constraint is far weaker than it looks:
 * `Partial<Record<K, V>>` makes every key optional, so a superset is assignable
 * to a subset and a wrong vocabulary compiles — as does widening the key type
 * back to `Record<string, Provenance>`. Both were tried and both passed. Since
 * `keyof Partial<Record<K, V>>` is exactly `K`, comparing the key sets each way
 * catches both, and the failure branches carry the missing or extra members so
 * the error names what is wrong.
 *
 * What it still cannot see, and why that is the right split: dropping a member
 * from a vocabulary *constant* changes both sides of the comparison at once, so
 * this stays green. That drift is between a vocabulary and the schema's own
 * fields, which is a runtime question — `attribute vocabularies match their
 * schemas` in `fact-provenance.test.ts` answers it, and was confirmed to fail
 * when broken.
 */
export type ExactKeys<Actual, Expected> = [Actual] extends [Expected]
  ? [Expected] extends [Actual]
    ? true
    : { vocabulary_is_missing: Exclude<Expected, Actual> }
  : { vocabulary_has_extra: Exclude<Actual, Expected> };

/**
 * `T extends Provenanced<A>` catches the field going missing; `ExactKeys`
 * catches a wrong or widened vocabulary. `AssertTrue` is what makes the second
 * half bite — an earlier version returned a sentinel *object* on failure and
 * accepted it, because an object satisfies `extends object`. It has to demand
 * `true`, and the failure branches must not be `never`, since `never` extends
 * everything and would pass silently.
 */
type AssertProvenanced<T extends Provenanced<A>, A extends string> = ExactKeys<
  keyof T['attribute_provenance'],
  A
>;

/**
 * Exported so `./predicate` can pin its own attribute table against the same
 * vocabularies, rather than declaring a second copy of this comparison. Two
 * copies of a rule are two rules, and this one is the rule that says a
 * vocabulary and its reader cannot drift apart.
 */
export type AssertTrue<T extends true> = T;
export type AssertedProvenancedObjects = [
  AssertTrue<AssertProvenanced<System, (typeof SYSTEM_ATTRIBUTES)[number]>>,
  AssertTrue<AssertProvenanced<Environment, (typeof ENVIRONMENT_ATTRIBUTES)[number]>>,
  AssertTrue<AssertProvenanced<Component, (typeof COMPONENT_ATTRIBUTES)[number]>>,
  AssertTrue<AssertProvenanced<Actor, (typeof ACTOR_ATTRIBUTES)[number]>>,
  AssertTrue<AssertProvenanced<Asset, (typeof ASSET_ATTRIBUTES)[number]>>,
  AssertTrue<AssertProvenanced<DataFlow, (typeof DATA_FLOW_ATTRIBUTES)[number]>>,
  AssertTrue<AssertProvenanced<ControlFlow, (typeof CONTROL_FLOW_ATTRIBUTES)[number]>>,
  AssertTrue<AssertProvenanced<TrustBoundary, (typeof TRUST_BOUNDARY_ATTRIBUTES)[number]>>,
  AssertTrue<AssertProvenanced<ExistingControl, (typeof EXISTING_CONTROL_ATTRIBUTES)[number]>>,
  AssertTrue<AssertProvenanced<SafetyFunction, (typeof SAFETY_FUNCTION_ATTRIBUTES)[number]>>,
];

export const CANONICAL_MODEL_VERSION = '1.0';

const CanonicalModelShape = z.object({
  model_version: z.literal(CANONICAL_MODEL_VERSION),
  tenant_id: TenantIdSchema,
  assessment_id: AssessmentIdSchema,

  system: SystemSchema,
  environments: z.array(EnvironmentSchema),
  components: z.array(ComponentSchema),
  actors: z.array(ActorSchema),
  assets: z.array(AssetSchema),
  data_flows: z.array(DataFlowSchema),
  control_flows: z.array(ControlFlowSchema),
  trust_boundaries: z.array(TrustBoundarySchema),
  controls: z.array(ExistingControlSchema),
  /** Reserved for the OT pack (docs/DECISIONS.md ZD-010) — empty in MVP. */
  safety_functions: z.array(SafetyFunctionSchema),

  /* Uncertainty carried forward from extraction and clarification. */
  assumptions: z.array(AssumptionSchema),
  unknowns: z.array(UnknownSchema),
  /** Contradictions between sources (docs/DECISIONS.md ZD-013). */
  conflicts: z.array(ConflictSchema),

  evidence_refs: z.array(EvidenceRefSchema),
  /**
   * How much of each source artefact was actually readable
   * (docs/DECISIONS.md ZD-025). Lifted from the extraction rather than
   * re-derived from storage, for ZD-014's reason.
   */
  source_coverage: z.array(ArtefactCoverageSchema),
  created_at: TimestampSchema,
});

export type CanonicalModel = z.infer<typeof CanonicalModelShape>;

export interface ModelIntegrityIssue {
  code:
    | 'duplicate_id'
    | 'dangling_reference'
    | 'contradicted_without_conflict'
    | 'conflict_names_unknown_object'
    | 'citation_without_locator'
    | 'attribute_without_provenance'
    | 'coverage_for_uncited_artefact'
    | 'duplicate_coverage';
  message: string;
  path: (string | number)[];
  id: string;
}

/**
 * ZD-015, enforced a second time on purpose.
 *
 * `EvidenceCitationSchema` already refuses an artefact citation with no
 * locator, so anything that *parses* is covered. This catches the other route
 * in: a `CanonicalModel` built directly in TypeScript and handed to
 * `checkModelIntegrity` without ever being parsed. TypeScript cannot express
 * "at least one of these three is non-null", so the type alone permits it —
 * and a rule that only holds on one of the two paths into the model is exactly
 * the kind of trapdoor ZD-015 was written about.
 */
function citationLocatorIssues(
  citation: EvidenceCitation,
  path: (string | number)[],
): ModelIntegrityIssue[] {
  if (idKindOf(citation.ref) !== 'artefact') return [];
  return locatorLocatesSomething(citation.locator)
    ? []
    : [
        {
          code: 'citation_without_locator',
          message: `citation of artefact ${citation.ref} must say where in the artefact the assertion comes from`,
          path,
          id: citation.ref,
        },
      ];
}

/**
 * One architecture object, as everything downstream needs to see it.
 *
 * It carries `attribute_provenance` as well as `provenance` because
 * `validateFinding` has to be able to ask how a *particular attribute* was
 * established, not just how the object was (docs/DECISIONS.md ZD-020). It
 * satisfies `Provenanced`, so `attributeConfidence` reads an entry from this
 * index exactly as it reads the object itself.
 *
 * `name` is `null` where an object genuinely has none — flows, control flows,
 * and an unnamed trust boundary (ZD-023).
 */
export interface CanonicalObjectEntry extends Provenanced {
  type: CanonicalObjectType;
  name: string | null;
  /**
   * The object itself, so a reader that needs an attribute's **value** rather
   * than its provenance does not have to walk the model's collections again
   * (docs/DECISIONS.md ZD-029). The predicate evaluator is the caller: its
   * attribute names are strings validated against the object type's vocabulary
   * at pack-parse time, so it reads them dynamically by design.
   */
  values: Readonly<Record<string, unknown>>;
}

export type CanonicalObjectIndex = ReadonlyMap<string, CanonicalObjectEntry>;

/**
 * Builds `id → { type, name, provenance, attribute_provenance }` for every
 * architecture object in the model.
 *
 * `validateFinding` resolves grounded ids through this map, so a finding can
 * only cite something the model actually contains — and reads the provenance it
 * finds there to derive confidence, rather than trusting a worker's word for it.
 */
export function indexCanonicalObjects(model: CanonicalModel): CanonicalObjectIndex {
  const index = new Map<string, CanonicalObjectEntry>();
  const add = (
    id: string,
    type: CanonicalObjectType,
    name: string | null,
    object: Provenanced,
  ): void => {
    index.set(id, {
      type,
      name,
      provenance: object.provenance,
      attribute_provenance: object.attribute_provenance,
      values: object as unknown as Record<string, unknown>,
    });
  };
  for (const o of model.components) add(o.component_id, 'component', o.name, o);
  for (const o of model.actors) add(o.actor_id, 'actor', o.name, o);
  for (const o of model.assets) add(o.asset_id, 'asset', o.name, o);
  for (const o of model.data_flows) add(o.flow_id, 'data_flow', null, o);
  for (const o of model.control_flows) add(o.ctrlflow_id, 'control_flow', null, o);
  for (const o of model.trust_boundaries) add(o.boundary_id, 'trust_boundary', o.name, o);
  for (const o of model.environments) add(o.environment_id, 'environment', o.name, o);
  for (const o of model.controls) add(o.control_id, 'existing_control', o.name, o);
  for (const o of model.safety_functions) add(o.safety_function_id, 'safety_function', o.name, o);
  return index;
}

/**
 * The trust boundaries a flow crosses (docs/DECISIONS.md ZD-027).
 *
 * > A boundary **B** is crossed by a flow **F** iff **exactly one** of F's
 * > endpoints is a member of `B.separates`.
 *
 * This is the whole rule, and it lives here rather than in the normaliser
 * because two things need it and they must not be able to disagree:
 * normalisation *derives* the attribute with it, and `checkModelIntegrity` uses
 * it to decide whether the attribute should have been derived at all. Two
 * copies of a rule are two rules.
 */
export function boundariesCrossedBy(
  boundaries: readonly TrustBoundary[],
  from: string,
  to: string,
): string[] {
  return boundaries
    .filter((boundary) => boundary.separates.includes(from) !== boundary.separates.includes(to))
    .map((boundary) => boundary.boundary_id);
}

/**
 * Whether the model places this flow relative to its boundary structure at all.
 *
 * The guard that keeps a derived empty honest (ZD-027). If no boundary mentions
 * either endpoint — including the case where the model has no boundaries — then
 * `crosses_boundary_ids: []` would not mean "crosses nothing", it would mean
 * "we could not tell", and a pack pattern reading the first when the second is
 * true is the missed material threat CLAUDE.md principle 9 names as the primary
 * failure mode. So the attribute is left unestablished instead, and this is
 * what tells the two apart.
 */
export function flowCrossingIsDerivable(
  boundaries: readonly TrustBoundary[],
  from: string,
  to: string,
): boolean {
  return boundaries.some(
    (boundary) => boundary.separates.includes(from) || boundary.separates.includes(to),
  );
}

/** How an observed control reaches the object it protects (ZD-030). */
export type ControlApplication = 'direct' | 'via_boundary';

export interface ControlCoverage {
  /** Controls that apply to the target, and how each one reaches it. */
  applies: { control_id: string; how: ControlApplication }[];
  /**
   * The attributes that make the coverage question undecidable — empty when it
   * is decidable. **Not the same as "no control applies"**, and the whole
   * reason this is a record rather than a list: a pack pattern reading
   * absence-of-control as a threat must not fire on a flow we simply failed to
   * place.
   *
   * **It carries the refs rather than a boolean, and that is the sds-04f
   * correction** (docs/DECISIONS.md ZD-041 §3). Two independent causes reach
   * this state — a flow whose `crosses_boundary_ids` was never derived in a
   * model holding a boundary-scoped control, and **any** control whose own
   * `applies_to` was never established — and a boolean cannot tell them apart,
   * so the caller had to guess. It guessed `crosses_boundary_ids` on the
   * subject, every time. That is wrong for the second cause always, and wrong
   * for *every non-flow subject in both directions*: a component has no
   * `crosses_boundary_ids` at all, so the clarification named an attribute that
   * does not exist; and where a flow's crossing **was** established, the
   * question asked the client for something the documents had already answered.
   * Fourteen refs across the corpus, and a clarification that says we cannot
   * establish a fact we hold is worse than silence — it is ZD-039's redirection
   * failure arriving in the question rather than in the finding.
   */
  undetermined: { object_id: string; attribute: string }[];
  /**
   * The attributes a **positive** answer read, so a predicate navigating through
   * it records them as witnesses (ZD-029 §5). Without this the `via_boundary`
   * route would be invisible to `depends_on_attributes`, and a finding resting
   * on an `inferred` crossing would read exactly as confident as one resting on
   * a control the document names against the flow itself.
   *
   * Only the controls that *apply*: one applying control is reason enough, and
   * citing the rest would be the over-collection ZD-020 exists to remove.
   */
  reads: { object_id: string; attribute: string }[];
  /**
   * The attributes a **negative** answer read — every control's `applies_to`.
   *
   * The asymmetry is real rather than tidy. "This control protects the flow"
   * rests on that one control; "nothing protects the flow" rests on every
   * control in the model being checked and found not to, so a control whose
   * `applies_to` is weakly established genuinely weakens the negative claim.
   * Most patterns that matter are negative ones — an absent control is the
   * finding — so getting this backwards would overstate confidence in exactly
   * the population it is most often computed for.
   */
  examined: { object_id: string; attribute: string }[];
}

/**
 * Which observed controls apply to one object (docs/DECISIONS.md ZD-030).
 *
 * > A control **C** applies to a flow **F** iff `F` is in `C.applies_to`
 * > (direct), or some boundary in `C.applies_to` is in `F.crosses_boundary_ids`
 * > (via boundary). Where `crosses_boundary_ids` is unestablished, the answer is
 * > **undetermined**, never "no".
 *
 * Boundary mediation is for flows only. A control stated at a boundary is about
 * *traffic crossing* it, not about the components the boundary separates, and
 * extending it to them would turn "TLS on the internet edge" into a claim that
 * every component inside the edge is protected by TLS — which is not what the
 * sentence says.
 */
export function controlsCovering(model: CanonicalModel, targetId: string): ControlCoverage {
  const applies: { control_id: string; how: ControlApplication }[] = [];
  const reads: { object_id: string; attribute: string }[] = [];
  const examined: { object_id: string; attribute: string }[] = model.controls.map((control) => ({
    object_id: control.control_id,
    attribute: 'applies_to',
  }));

  const flow =
    model.data_flows.find((f) => f.flow_id === targetId) ??
    model.control_flows.find((f) => f.ctrlflow_id === targetId) ??
    null;
  const crossingEstablished =
    flow !== null && establishedAttributes(flow).includes('crosses_boundary_ids');
  const crossed = new Set(flow?.crosses_boundary_ids ?? []);

  let boundaryScopedExists = false;
  const undetermined: { object_id: string; attribute: string }[] = [];

  for (const control of model.controls) {
    if (control.applies_to.includes(targetId)) {
      applies.push({ control_id: control.control_id, how: 'direct' });
      reads.push({ object_id: control.control_id, attribute: 'applies_to' });
      continue;
    }
    // A control whose scope the model never worked out could apply to anything,
    // so "nothing protects this" is undecidable while one exists (ZD-027 rule 2
    // one object along). Normalisation leaves exactly this state behind when a
    // control names something it could not resolve: `applies_to` empty, with no
    // provenance record.
    //
    // **One ref per such control, not one for the set**, on ZD-029 §5's rule
    // for negatives: a negative rests on every control having been checked, so
    // every control that could not be checked is separately a reason the answer
    // is missing — and the question the client is then asked names each of them.
    if (!establishedAttributes(control).includes('applies_to')) {
      undetermined.push({ object_id: control.control_id, attribute: 'applies_to' });
      continue;
    }

    const boundaries = control.applies_to.filter((id) => idKindOf(id) === 'trust_boundary');
    if (boundaries.length === 0 || flow === null) continue;

    boundaryScopedExists = true;
    if (!crossingEstablished) continue;
    if (!boundaries.some((id) => crossed.has(id))) continue;

    applies.push({ control_id: control.control_id, how: 'via_boundary' });
    reads.push({ object_id: control.control_id, attribute: 'applies_to' });
    reads.push({ object_id: targetId, attribute: 'crosses_boundary_ids' });
  }

  // The other cause, and the only one that legitimately names the target: the
  // model holds a boundary-scoped control and never placed this flow, so the
  // mediated route cannot be evaluated. It is a flow attribute by construction,
  // because `boundaryScopedExists` can only be set where `flow !== null`.
  if (boundaryScopedExists && !crossingEstablished) {
    undetermined.push({ object_id: targetId, attribute: 'crosses_boundary_ids' });
  }
  if (flow !== null && crossingEstablished && boundaryScopedExists) {
    examined.push({ object_id: targetId, attribute: 'crosses_boundary_ids' });
  }
  return { applies, undetermined, reads, examined };
}

/**
 * A label for a trust boundary, for a human reading a report or a review screen
 * (docs/DECISIONS.md ZD-023).
 *
 * **This is presentation, not a fact.** It is computed on demand and stored
 * nowhere, which is the entire point: normalisation deriving a name and writing
 * it into the model would put a string there that reads in a report exactly
 * like one the document supplied, and that a later worker could ground or match
 * on. An unnamed boundary is identified by what it separates, so that is what
 * the label says.
 */
export function describeBoundary(model: CanonicalModel, boundary: TrustBoundary): string {
  if (boundary.name !== null) return boundary.name;

  const objects = indexCanonicalObjects(model);
  const names = boundary.separates.map((id) => objects.get(id)?.name ?? id);

  if (names.length === 0) return 'unnamed boundary';
  if (names.length === 1) return `unnamed boundary around ${names[0]!}`;
  if (names.length === 2) return `unnamed boundary between ${names[0]!} and ${names[1]!}`;
  return `unnamed boundary around ${names[0]!}, ${names[1]!} and ${String(names.length - 2)} more`;
}

/**
 * Referential integrity for the canonical model: no duplicate ids, and every
 * cross-reference resolves to an object in the same model.
 *
 * This is grounding-by-schema applied one level below findings. If the model
 * itself can contain a dangling reference, then "the finding cites a real object
 * id" stops meaning very much.
 */
export function checkModelIntegrity(model: CanonicalModel): ModelIntegrityIssue[] {
  const issues: ModelIntegrityIssue[] = [];
  const seen = new Set<string>();

  const declare = (id: string, path: (string | number)[]): void => {
    if (seen.has(id)) {
      issues.push({ code: 'duplicate_id', message: `duplicate object id ${id}`, path, id });
    }
    seen.add(id);
  };

  model.environments.forEach((o, i) => declare(o.environment_id, ['environments', i]));
  model.components.forEach((o, i) => declare(o.component_id, ['components', i]));
  model.actors.forEach((o, i) => declare(o.actor_id, ['actors', i]));
  model.assets.forEach((o, i) => declare(o.asset_id, ['assets', i]));
  model.data_flows.forEach((o, i) => declare(o.flow_id, ['data_flows', i]));
  model.control_flows.forEach((o, i) => declare(o.ctrlflow_id, ['control_flows', i]));
  model.trust_boundaries.forEach((o, i) => declare(o.boundary_id, ['trust_boundaries', i]));
  model.controls.forEach((o, i) => declare(o.control_id, ['controls', i]));
  model.safety_functions.forEach((o, i) => declare(o.safety_function_id, ['safety_functions', i]));
  model.assumptions.forEach((o, i) => declare(o.assumption_id, ['assumptions', i]));
  model.unknowns.forEach((o, i) => declare(o.unknown_id, ['unknowns', i]));
  model.conflicts.forEach((o, i) => declare(o.conflict_id, ['conflicts', i]));

  const known = indexCanonicalObjects(model);
  const assumptionIds = new Set(model.assumptions.map((a) => a.assumption_id));
  const evidenceRefs = new Set(model.evidence_refs);

  const requireObject = (id: string, path: (string | number)[]): void => {
    if (!known.has(id)) {
      issues.push({ code: 'dangling_reference', message: `unknown object id ${id}`, path, id });
    }
  };

  /** One provenance record, wherever it hangs. `path` already names it. */
  const checkOneProvenance = (p: Provenance, path: (string | number)[]): void => {
    p.evidence_citations.forEach((citation, i) => {
      if (!evidenceRefs.has(citation.ref)) {
        issues.push({
          code: 'dangling_reference',
          message: `provenance cites evidence ${citation.ref}, which is not listed on the model`,
          path: [...path, 'evidence_citations', i, 'ref'],
          id: citation.ref,
        });
      }
      issues.push(
        ...citationLocatorIssues(citation, [...path, 'evidence_citations', i, 'locator']),
      );
    });
    p.derived_from_assumption_ids.forEach((id, i) => {
      if (!assumptionIds.has(id)) {
        issues.push({
          code: 'dangling_reference',
          message: `provenance cites unknown assumption ${id}`,
          path: [...path, 'derived_from_assumption_ids', i],
          id,
        });
      }
    });
  };

  /**
   * Every provenance record an object carries — its own, and one per attribute
   * that was separately established (docs/DECISIONS.md ZD-020). Checking only
   * the object's own would leave attribute citations unvalidated, which is the
   * hole the per-attribute shape would otherwise open: the citations that matter
   * most are exactly the ones attached to a disputed attribute.
   */
  const checkProvenance = <A extends string>(
    object: Provenanced<A>,
    attributes: readonly A[],
    path: (string | number)[],
  ): void => {
    checkOneProvenance(object.provenance, [...path, 'provenance']);
    for (const attribute of establishedAttributes(object)) {
      const provenance = object.attribute_provenance[attribute];
      if (provenance === undefined) continue;
      checkOneProvenance(provenance, [...path, 'attribute_provenance', attribute]);
    }

    // Inheritance may only weaken (docs/DECISIONS.md ZD-020). An attribute
    // holding an absence-marker asserts nothing and inherits; one asserting a
    // real value has to say where it came from, or the object's own basis —
    // which answers "does this thing exist" — would silently be read as
    // evidence for a claim about it.
    for (const attribute of attributesMissingProvenance(object, attributes)) {
      issues.push({
        code: 'attribute_without_provenance',
        message: `${attribute} asserts a value but carries no provenance of its own; it would inherit the object's, which records only how the object's existence was established`,
        path: [...path, 'attribute_provenance', attribute],
        id: String(attribute),
      });
    }
  };

  /**
   * A derived attribute must be established when the model shows it *could*
   * have been derived (docs/DECISIONS.md ZD-027).
   *
   * `attributesMissingProvenance` cannot catch this one, and the reason is the
   * whole point of the rule: `crosses_boundary_ids: []` with a provenance record
   * says "this flow crosses nothing", and `[]` without one says "nobody worked
   * it out". Identical values, opposite meanings, and `isAbsenceMarker` only
   * ever sees the value. So the question asked here is not about the value at
   * all — it is whether the model placed the flow. If it did, the derivation had
   * everything it needed and its absence is a defect; if it did not, the absence
   * is the honest answer.
   */
  const checkDerivedCrossing = (
    flow: { from: string; to: string; attribute_provenance: { crosses_boundary_ids?: Provenance } },
    id: string,
    path: (string | number)[],
  ): void => {
    if (!flowCrossingIsDerivable(model.trust_boundaries, flow.from, flow.to)) return;
    if (flow.attribute_provenance.crosses_boundary_ids !== undefined) return;
    issues.push({
      code: 'attribute_without_provenance',
      message: `crosses_boundary_ids carries no provenance on ${id}, but the model places at least one of its endpoints inside a trust boundary — so the derivation could have run, and an empty value with no record is indistinguishable from one that never did`,
      path: [...path, 'attribute_provenance', 'crosses_boundary_ids'],
      id,
    });
  };

  checkProvenance(model.system, SYSTEM_ATTRIBUTES, ['system']);

  model.environments.forEach((o, i) =>
    checkProvenance(o, ENVIRONMENT_ATTRIBUTES, ['environments', i]),
  );
  model.actors.forEach((o, i) => checkProvenance(o, ACTOR_ATTRIBUTES, ['actors', i]));

  model.components.forEach((o, i) => {
    checkProvenance(o, COMPONENT_ATTRIBUTES, ['components', i]);
    if (o.environment_id !== null) {
      requireObject(o.environment_id, ['components', i, 'environment_id']);
    }
  });

  model.assets.forEach((o, i) => {
    checkProvenance(o, ASSET_ATTRIBUTES, ['assets', i]);
    o.resides_in.forEach((id, j) => requireObject(id, ['assets', i, 'resides_in', j]));
  });

  model.data_flows.forEach((o, i) => {
    checkProvenance(o, DATA_FLOW_ATTRIBUTES, ['data_flows', i]);
    checkDerivedCrossing(o, o.flow_id, ['data_flows', i]);
    requireObject(o.from, ['data_flows', i, 'from']);
    requireObject(o.to, ['data_flows', i, 'to']);
    o.asset_ids.forEach((id, j) => requireObject(id, ['data_flows', i, 'asset_ids', j]));
    o.crosses_boundary_ids.forEach((id, j) =>
      requireObject(id, ['data_flows', i, 'crosses_boundary_ids', j]),
    );
  });

  model.control_flows.forEach((o, i) => {
    checkProvenance(o, CONTROL_FLOW_ATTRIBUTES, ['control_flows', i]);
    checkDerivedCrossing(o, o.ctrlflow_id, ['control_flows', i]);
    requireObject(o.from, ['control_flows', i, 'from']);
    requireObject(o.to, ['control_flows', i, 'to']);
    o.crosses_boundary_ids.forEach((id, j) =>
      requireObject(id, ['control_flows', i, 'crosses_boundary_ids', j]),
    );
  });

  model.trust_boundaries.forEach((o, i) => {
    checkProvenance(o, TRUST_BOUNDARY_ATTRIBUTES, ['trust_boundaries', i]);
    o.separates.forEach((id, j) => requireObject(id, ['trust_boundaries', i, 'separates', j]));
  });

  model.controls.forEach((o, i) => {
    checkProvenance(o, EXISTING_CONTROL_ATTRIBUTES, ['controls', i]);
    o.applies_to.forEach((id, j) => requireObject(id, ['controls', i, 'applies_to', j]));
  });

  model.safety_functions.forEach((o, i) => {
    checkProvenance(o, SAFETY_FUNCTION_ATTRIBUTES, ['safety_functions', i]);
  });

  // --- Provenance ↔ conflict invariant (ZD-013, sharpened by ZD-020) --------
  // Every *attribute* whose basis is `contradicted` must have a conflict record
  // naming that object **and that attribute**, and every conflict must name a
  // real object. The attribute-level form is what makes `Conflict.attribute`
  // load-bearing rather than decorative: before ZD-020 a conflict about
  // `internet_facing` was satisfied by any conflict mentioning the object.
  const objectsByPath: { id: string; object: Provenanced; path: (string | number)[] }[] = [
    { id: SYSTEM_OBJECT_ID, object: model.system, path: ['system'] },
    ...model.environments.map((o, i) => ({
      id: o.environment_id,
      object: o,
      path: ['environments', i],
    })),
    ...model.components.map((o, i) => ({
      id: o.component_id,
      object: o,
      path: ['components', i],
    })),
    ...model.actors.map((o, i) => ({
      id: o.actor_id,
      object: o,
      path: ['actors', i],
    })),
    ...model.assets.map((o, i) => ({
      id: o.asset_id,
      object: o,
      path: ['assets', i],
    })),
    ...model.data_flows.map((o, i) => ({
      id: o.flow_id,
      object: o,
      path: ['data_flows', i],
    })),
    ...model.control_flows.map((o, i) => ({
      id: o.ctrlflow_id,
      object: o,
      path: ['control_flows', i],
    })),
    ...model.trust_boundaries.map((o, i) => ({
      id: o.boundary_id,
      object: o,
      path: ['trust_boundaries', i],
    })),
    ...model.controls.map((o, i) => ({
      id: o.control_id,
      object: o,
      path: ['controls', i],
    })),
    ...model.safety_functions.map((o, i) => ({
      id: o.safety_function_id,
      object: o,
      path: ['safety_functions', i],
    })),
  ];

  const conflictedAttributes = new Map<string, Set<string>>();
  for (const conflict of model.conflicts) {
    const attributes = conflictedAttributes.get(conflict.about_object_id) ?? new Set<string>();
    attributes.add(conflict.attribute);
    conflictedAttributes.set(conflict.about_object_id, attributes);
  }

  objectsByPath.forEach(({ id, object, path }) => {
    // The object's own basis is about its *existence*: two sources disagreeing
    // over whether this thing is in the architecture at all. Any conflict naming
    // it satisfies that, because there is no attribute to match on.
    if (object.provenance.basis === 'contradicted' && !conflictedAttributes.has(id)) {
      issues.push({
        code: 'contradicted_without_conflict',
        message: `${id} has provenance basis "contradicted" but no conflict record names it`,
        path: [...path, 'provenance', 'basis'],
        id,
      });
    }

    for (const [attribute, provenance] of Object.entries(object.attribute_provenance)) {
      if (provenance?.basis !== 'contradicted') continue;
      if (conflictedAttributes.get(id)?.has(attribute)) continue;
      issues.push({
        code: 'contradicted_without_conflict',
        message: `${id}.${attribute} has provenance basis "contradicted" but no conflict record names that object and attribute`,
        path: [...path, 'attribute_provenance', attribute, 'basis'],
        id,
      });
    }
  });

  model.conflicts.forEach((conflict, i) => {
    if (conflict.about_object_id !== SYSTEM_OBJECT_ID && !known.has(conflict.about_object_id)) {
      issues.push({
        code: 'conflict_names_unknown_object',
        message: `conflict ${conflict.conflict_id} names unknown object ${conflict.about_object_id}`,
        path: ['conflicts', i, 'about_object_id'],
        id: conflict.about_object_id,
      });
    }
    // A conflict is the place a locator matters most: its whole content is
    // "these sources disagree, here and here" (ZD-015).
    conflict.competing_assertions.forEach((assertion, j) => {
      issues.push(
        ...citationLocatorIssues(assertion.citation, [
          'conflicts',
          i,
          'competing_assertions',
          j,
          'citation',
          'locator',
        ]),
      );
    });
  });

  // --- Source coverage (docs/DECISIONS.md ZD-025) ----------------------------
  // Coverage describes what we could read of an artefact, so it has to be about
  // an artefact the model actually cites. An orphaned coverage row would put a
  // denominator in a report for a document that is not part of this assessment.
  const coveredArtefacts = new Set<string>();
  model.source_coverage.forEach((coverage, i) => {
    if (coveredArtefacts.has(coverage.artefact_id)) {
      issues.push({
        code: 'duplicate_coverage',
        message: `source_coverage names ${coverage.artefact_id} more than once`,
        path: ['source_coverage', i, 'artefact_id'],
        id: coverage.artefact_id,
      });
    }
    coveredArtefacts.add(coverage.artefact_id);

    if (!evidenceRefs.has(coverage.artefact_id)) {
      issues.push({
        code: 'coverage_for_uncited_artefact',
        message: `source_coverage describes ${coverage.artefact_id}, which is not listed on the model`,
        path: ['source_coverage', i, 'artefact_id'],
        id: coverage.artefact_id,
      });
    }
  });

  return issues;
}

export const CanonicalModelSchema = CanonicalModelShape.superRefine((model, ctx) => {
  for (const issue of checkModelIntegrity(model)) {
    ctx.addIssue({ code: 'custom', message: issue.message, path: issue.path });
  }
});
